import React, {useEffect, useState} from 'react'
import Pagination from '../../components/Pagination'
import {useUseWaitinListRequest} from '../../../hooks/waitingList/useWaitingList'
import { MenuComponent } from '../../../../_metronic/assets/ts/components'
import { formatDateShortIntl2 } from '../../../helpers/FormatDate'
import { usePagination } from '../../../hooks/utils/usePagination'
import { membershipDetails } from '../../../services/waitingListService'
import { showErrorAlert } from '../../../helpers/alert'
import { DetailWaitingListModal } from './DetailWaitingListModal'
import { DatatableComponent } from '../../../helpers/DatatableComponent'

export interface WaitlistUser {
  id: string
  fullName: string
  source: string
  country: string
  requested: string
  category: string
  assigned: string
  status: string
}

const PLACEHOLDER_ROWS = 8

const WaitingListGrid: React.FC = () => {
  const limit = 10
  const [timeoutWaitingList, setTimeoutWaitingList] = useState(10)
  const [activeTab, setActiveTab] = useState('pending')
  const [searchTerm, setSearchTerm] = useState('')
  const [reload, setReload] = useState<number>(Math.random() * limit)
  const {data, loading} = useUseWaitinListRequest(reload,activeTab,searchTerm,timeoutWaitingList)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [openDetailModal, setOpenDetailModal] = useState(false)
  const [countNews, setCountNews] = useState<number>(0)
  const {
    page,
    totalPages,
    currentData,
    setPage
  } = usePagination(data ?? [], limit);

  // escuchamos cambios en el estado del modal
  useEffect(() => {
    // demos recargar el grid solo cuando se cierre el panel detalle de información
    if(!openDetailModal){
      setPage(1) // reset page
      setTimeoutWaitingList(10) // ajustamos el timeout para que sea rápido
      setReload(Math.random() * 20)
    }
  }, [openDetailModal, setPage]);
  
  // escuchamos cambios en el input del buscador
  useEffect(() => {
    // demos recargar el grid cuando buscamos por texto
    setPage(1) // reset page
    setTimeoutWaitingList(2000) // ajustamos el timeout para que sea lento y de tiempo de escribir
    setReload(Math.random() * 20)
  }, [searchTerm, setPage]);

  useEffect(() => {
    MenuComponent.reinitialization();
    if(activeTab === 'pending'){
      setCountNews(data?.length ?? 0);
    }
  }, [activeTab, data]);

  const changeTab = (status: string) => {
    setActiveTab(status);
    setPage(1) // reset page
    setTimeoutWaitingList(10) // ajustamos el timeout para que sea rápido
    setReload(Math.random() * 20)
  }

  const searchInList = (event:any) => {
    if (event.key === 'Enter') {
      setPage(1) // reset page
      setTimeoutWaitingList(10) // ajustamos el timeout para que sea rápido
      setReload(Math.random() * 20)
    }
  }

  const renderCells = (
    items: any[],
    isLoading: boolean,
    getValue: (u: any) => React.ReactNode,
    keyPrefix: string,
    skeletonWidth: string = '70%',
    styleCell: 'table__cell--bold'|'' = 'table__cell--bold',
    isDate:boolean = false,
  ) => {
    if (isLoading) {
      return Array.from({length: PLACEHOLDER_ROWS}).map((_, index) => (
        <div
          key={`${keyPrefix}-${index}`}
          className={`table__cell ${index === 0 ? 'table__cell--bold' : ''}`}
        >
          <span className='skeleton skeleton--text' style={{width: skeletonWidth}} />
        </div>
      ))
    }
    return items.map((u, index) => (
      <div onClick={drawerOpen.bind(null, parseInt(u.id))}
        key={`${keyPrefix}-${index}`}
        className={`cursor-pointer table__cell ${styleCell}`}>
        {isDate ? formatDateShortIntl2(getValue(u)) : getValue(u)}
      </div>
    ))
  }

  const drawerOpen = async (user: number) => {
    try {
      const detailsMember = await membershipDetails(user)
      if (detailsMember) {
        setSelectedUser(detailsMember)
        setOpenDetailModal(true)
      }
    } catch (error: any) {
      showErrorAlert({title: 'Error', message: error?.message ?? ''})
      setOpenDetailModal(false)
    }
  }

  const handleCloseDetailModal = (open: boolean) => {
    setOpenDetailModal(open)
    if (!open) {
      setSelectedUser(null)
    }
  }

  return (
    <>
      <div className='users-waitlist-page'>
        <div className='users-waitlist-page__container'>
          <h1 className='users-waitlist__title'>Users Waitlist</h1>
          <div className="card">
            <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-bottom-0" style={{ overflow: 'auto', flexWrap: 'nowrap' }}>
              <li className="nav-item">
                <button type='button' className={`nav-link ${activeTab === 'pending' ? "active" : ""}`}
                  onClick={()=>changeTab('pending')}>
                  New
                  <div data-property-1="regular" className='badge-wrapper'>
                    <div className='badge-count'>{countNews}</div>
                  </div>
                </button>
              </li>
              <li className="separator-vertical">
                <hr />
              </li>
              <li className="nav-item">
                <button type='button' className={`nav-link ${activeTab === 'approved' ? "active" : ""}`}
                  onClick={()=>{changeTab('approved')}}>
                  Approved
                </button>
              </li>
              <li className="separator-vertical">
                <hr />
              </li>
              <li className="nav-item">
                <button type='button' className={`nav-link ${activeTab === 'rejected' ? "active" : ""}`}
                  onClick={()=>{changeTab('rejected')}}>
                  Rejected
                </button>
              </li>
            </ul>
          </div>

          {/* Barra de carga superior */}
          {loading && <div className='page-loading-bar' />}

          <div className='users-waitlist__content'>
            {/* Search Bar */}
            <div className='search-bar'>
              <div className='search-bar__icon'>
                <div className='search-icon__circle'></div>
                <div className='search-icon__handle'></div>
              </div>
              <input
                type='text'
                className='search-bar__input'
                placeholder='Search Forum'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { searchInList(e) }}
              />
            </div>

            {/* Table */}
            <DatatableComponent 
              columns={[
                {
                  label: 'ID',
                  key: 'id',
                  getValue: (row) => <>{`#${row.id}`}</>,
                  className: 'cursor-pointer',
                },
                {
                  label: 'NAME',
                  key: 'fullName',
                  className: 'cursor-pointer',
                },
                {
                  label: 'SOURCE',
                  key: 'source',
                  className: 'cursor-pointer',
                },
                {
                  label: 'CITY',
                  key: 'city',
                  className: 'cursor-pointer',
                },
                {
                  label: 'REQUESTED ON',
                  key: 'requested',
                  getValue: (row) => <>{formatDateShortIntl2(row.requested)}</>,
                  className: 'cursor-pointer',
                },
                {
                  label: 'CATEGORY',
                  key: 'category',
                  className: 'cursor-pointer',
                },
                {
                  label: 'ASSIGNED TO',
                  key: 'assigned',
                  className: 'cursor-pointer',
                }
              ]}
              tableClassName='waitinglist-table'
              data={currentData}
              isLoading={loading}
              currentPage={page}
              onClickRow={row => drawerOpen(row.id)}
              onPageChange={setPage}
              totalPages={totalPages}
            />
          </div>
          <DetailWaitingListModal
            open={openDetailModal}
            onClose={handleCloseDetailModal}
            dataUser={selectedUser}
          />
        </div>
      </div>
    </>
  )
}

export default WaitingListGrid
